import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'sonner';
import {
  Trophy, Flame, Award, Crown, Activity, Target,
  TrendingUp, BarChart3, MessageCircle, Users,
  CheckCircle, Lock, Star, Dumbbell, Heart,
  Pencil, FileText, Rocket, Flag, Zap,
  Layers, Shield, Gem, Diamond, Swords,
  Clipboard, UtensilsCrossed, ShoppingBag, ChevronLeft, ChevronRight,
  CalendarDays, Medal
} from 'lucide-react';
import { Card, CardContent } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Progress } from './components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './components/ui/tooltip';
import { BACKEND_URL, API } from './config';

// Icon lookup for badge icons (lucide names -> components)
const ICON_MAP = {
  'pencil': Pencil, 'dumbbell': Dumbbell, 'rocket': Rocket, 'heart': Heart,
  'flag': Flag, 'flame': Flame, 'swords': Swords, 'file-text': FileText,
  'zap': Zap, 'trending-up': TrendingUp, 'message-circle': MessageCircle,
  'layers': Layers, 'shield': Shield, 'trophy': Trophy, 'crown': Crown,
  'gem': Gem, 'diamond': Diamond, 'star': Star, 'award': Award, 'medal': Medal,
  'clipboard': Clipboard, 'utensils': UtensilsCrossed, 'shopping-bag': ShoppingBag,
};

const getIconComponent = (iconName) => ICON_MAP[iconName] || Award;

// Rarity border/glow styles
const RARITY_STYLES = {
  common:    { ring: 'ring-gray-300',   glow: '', label: 'Common',    textColor: 'text-gray-500',   bgGradient: 'from-gray-50 to-gray-100',   border: 'border-gray-200' },
  uncommon:  { ring: 'ring-emerald-400', glow: '', label: 'Uncommon',  textColor: 'text-emerald-600', bgGradient: 'from-emerald-50 to-teal-50', border: 'border-emerald-200' },
  rare:      { ring: 'ring-blue-400',    glow: 'shadow-blue-100',  label: 'Rare',      textColor: 'text-blue-600',    bgGradient: 'from-blue-50 to-indigo-50', border: 'border-blue-200' },
  epic:      { ring: 'ring-purple-400',  glow: 'shadow-purple-100', label: 'Epic',      textColor: 'text-purple-600',  bgGradient: 'from-purple-50 to-fuchsia-50', border: 'border-purple-200' },
  legendary: { ring: 'ring-amber-400',   glow: 'shadow-amber-200', label: 'Legendary', textColor: 'text-amber-600',   bgGradient: 'from-amber-50 to-yellow-50',  border: 'border-amber-300' },
};

// ============================================================
// BadgeCard — Individual badge display with rarity + ownership %
// ============================================================
export const BadgeCard = ({ badge, isEarned = false, size = 'md', showOwnership = true }) => {
  const rarity = badge.rarity || 'common';
  const style = RARITY_STYLES[rarity] || RARITY_STYLES.common;
  const IconComp = getIconComponent(badge.icon);
  const pct = badge.owners_percentage;

  const sizeConfig = {
    sm: { card: 'p-2.5', icon: 'h-5 w-5', text: 'text-[11px]', title: 'text-xs' },
    md: { card: 'p-4', icon: 'h-7 w-7', text: 'text-xs', title: 'text-sm' },
    lg: { card: 'p-5', icon: 'h-9 w-9', text: 'text-sm', title: 'text-base' },
  }[size];

  return (
    <div
      className={`relative rounded-xl text-center transition-all duration-200 ${sizeConfig.card} ${
        isEarned
          ? `bg-gradient-to-b ${style.bgGradient} ${style.border} border shadow-sm ${style.glow} hover:shadow-md`
          : 'bg-gray-50/80 border border-gray-200 opacity-50 grayscale-[40%]'
      }`}
      data-testid={`badge-card-${badge.id || badge.badge_id}`}
    >
      {/* Rarity indicator dot */}
      <div
        className={`absolute top-2 right-2 w-2 h-2 rounded-full ${isEarned ? '' : 'opacity-30'}`}
        style={{ backgroundColor: badge.rarity_config?.color || '#6B7280' }}
        title={style.label}
      />

      {/* Icon */}
      <div className={`mx-auto mb-2 ${isEarned ? '' : ''}`}>
        {isEarned ? (
          <IconComp className={`${sizeConfig.icon} mx-auto`} style={{ color: badge.rarity_config?.color || '#6B7280' }} />
        ) : (
          <Lock className={`${sizeConfig.icon} mx-auto text-gray-300`} />
        )}
      </div>

      {/* Name */}
      <h4 className={`font-semibold ${sizeConfig.title} ${isEarned ? 'text-gray-900' : 'text-gray-400'} leading-tight mb-0.5`}>
        {badge.name}
      </h4>

      {/* Description */}
      <p className={`${sizeConfig.text} text-gray-500 leading-snug mb-2 line-clamp-2`}>
        {badge.description}
      </p>

      {/* Rarity tag + ownership */}
      <div className="space-y-1">
        <span
          className={`inline-block ${sizeConfig.text} font-medium px-2 py-0.5 rounded-full`}
          style={{
            color: badge.rarity_config?.color || '#6B7280',
            backgroundColor: (badge.rarity_config?.bg || '#F3F4F6') + 'CC',
            border: `1px solid ${badge.rarity_config?.border || '#D1D5DB'}`,
          }}
        >
          {style.label}
        </span>
        {showOwnership && pct !== undefined && (
          <p className={`${sizeConfig.text} text-gray-400`}>
            {pct > 0 ? `Owned by ${pct}% of users` : 'Not yet earned by anyone'}
          </p>
        )}
      </div>

      {/* Earned date */}
      {isEarned && badge.earned_at && (
        <p className="text-[10px] text-gray-400 mt-1">
          {new Date(badge.earned_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </p>
      )}
    </div>
  );
};

// ============================================================
// ProfileBadgeSection — Earned badges displayed on user profile
// ============================================================
export const ProfileBadgeSection = ({ userId }) => {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    const fetchBadges = async () => {
      try {
        const resp = await axios.get(`${API}/users/${userId}/badges`);
        setBadges(resp.data || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    fetchBadges();
  }, [userId]);

  if (loading) return null;
  if (badges.length === 0) return null;

  // Sort: legendary first, then epic, rare, uncommon, common
  const rarityOrder = { legendary: 0, epic: 1, rare: 2, uncommon: 3, common: 4 };
  const sorted = [...badges].sort((a, b) => (rarityOrder[a.rarity] ?? 5) - (rarityOrder[b.rarity] ?? 5));

  return (
    <div className="space-y-3" data-testid="profile-badges-section">
      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
        <Trophy className="h-4 w-4 text-amber-500" />
        Badges Earned
        <span className="text-sm font-normal text-gray-400">({badges.length})</span>
      </h3>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
        {sorted.map(badge => (
          <BadgeCard
            key={badge.instance_id || badge.badge_id}
            badge={badge}
            isEarned={true}
            size="sm"
            showOwnership={false}
          />
        ))}
      </div>
    </div>
  );
};

// ============================================================
// GamificationDashboard — Full progress + badges modal
// ============================================================
export const GamificationDashboard = ({ open, onClose }) => {
  const [progress, setProgress] = useState(null);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const sessionToken = Cookies.get('session_token');
      const headers = { Authorization: `Bearer ${sessionToken}` };

      const [progressRes, badgesRes, catalogRes] = await Promise.all([
        axios.get(`${API}/user/progress`, { headers }),
        axios.get(`${API}/user/badges`, { headers }),
        axios.get(`${API}/badges/catalog`),
      ]);
      setProgress(progressRes.data);
      setEarnedBadges(badgesRes.data || []);
      setCatalog(catalogRes.data || []);
    } catch (err) {
      console.error('Gamification fetch error:', err);
    }
    setLoading(false);
  };

  // Build earned set (handle both old emoji names and new IDs)
  const earnedSet = useMemo(() => {
    const s = new Set();
    for (const b of earnedBadges) {
      s.add(b.badge_id);
      s.add(b.badge_name);
    }
    return s;
  }, [earnedBadges]);

  const isEarned = (badge) => earnedSet.has(badge.id) || earnedSet.has(badge.name);

  const filteredCatalog = activeFilter === 'all'
    ? catalog
    : catalog.filter(b => b.rarity === activeFilter);

  const rarityFilters = ['all', 'common', 'uncommon', 'rare', 'epic', 'legendary'];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="gamification-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Trophy className="h-5 w-5 text-amber-500" />
            <span>Your Fitness Journey</span>
          </DialogTitle>
          <DialogDescription>Track your progress, earn badges, and level up</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-4 py-8">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse"><div className="h-4 bg-gray-200 rounded w-3/4 mb-2" /><div className="h-20 bg-gray-200 rounded" /></div>
            ))}
          </div>
        ) : (
          <div className="overflow-y-auto max-h-[70vh] space-y-6 pr-1">
            {/* Progress Overview */}
            {progress && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="text-center p-3 border-amber-200 bg-gradient-to-b from-amber-50 to-yellow-50">
                  <Crown className="h-6 w-6 text-amber-600 mx-auto mb-1" />
                  <div className="text-xl font-bold text-amber-600">Level {progress.level}</div>
                  <p className="text-xs text-gray-500">{progress.total_points} XP</p>
                </Card>
                <Card className="text-center p-3 border-orange-200 bg-gradient-to-b from-orange-50 to-red-50">
                  <Flame className="h-6 w-6 text-orange-500 mx-auto mb-1" />
                  <div className="text-xl font-bold text-orange-500">{progress.current_streak}</div>
                  <p className="text-xs text-gray-500">Day Streak</p>
                </Card>
                <Card className="text-center p-3 border-blue-200 bg-gradient-to-b from-blue-50 to-indigo-50">
                  <Dumbbell className="h-6 w-6 text-blue-600 mx-auto mb-1" />
                  <div className="text-xl font-bold text-blue-600">{progress.workouts_completed}</div>
                  <p className="text-xs text-gray-500">Workouts</p>
                </Card>
                <Card className="text-center p-3 border-purple-200 bg-gradient-to-b from-purple-50 to-fuchsia-50">
                  <Trophy className="h-6 w-6 text-purple-600 mx-auto mb-1" />
                  <div className="text-xl font-bold text-purple-600">{earnedBadges.length}</div>
                  <p className="text-xs text-gray-500">Badges</p>
                </Card>
              </div>
            )}

            {/* Badges Catalog */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold flex items-center gap-2">
                  <Award className="h-4 w-4 text-purple-600" />
                  Badges & Achievements
                </h3>
                <span className="text-xs text-gray-400">{earnedBadges.length}/{catalog.length} earned</span>
              </div>

              {/* Rarity Filters */}
              <div className="flex gap-1.5 flex-wrap">
                {rarityFilters.map(f => {
                  const rStyle = f === 'all' ? null : RARITY_STYLES[f];
                  return (
                    <button
                      key={f}
                      onClick={() => setActiveFilter(f)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                        activeFilter === f
                          ? f === 'all' ? 'bg-gray-900 text-white' : `${rStyle.textColor} ring-1 ${rStyle.ring} bg-white`
                          : 'text-gray-500 hover:bg-gray-100'
                      }`}
                      data-testid={`badge-filter-${f}`}
                    >
                      {f === 'all' ? 'All' : RARITY_STYLES[f]?.label}
                    </button>
                  );
                })}
              </div>

              {/* Badge Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {filteredCatalog.map(badge => (
                  <BadgeCard
                    key={badge.id}
                    badge={badge}
                    isEarned={isEarned(badge)}
                    size="md"
                    showOwnership={true}
                  />
                ))}
              </div>
            </div>

            {/* Progress Bars */}
            {progress && (
              <div className="space-y-3">
                <h3 className="text-base font-semibold flex items-center gap-2">
                  <Target className="h-4 w-4 text-blue-600" />
                  Progress Tracking
                </h3>
                <div className="grid md:grid-cols-2 gap-3">
                  <Card className="p-3">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="font-medium text-sm">Streak</span>
                      <span className="text-xs text-gray-500">{progress.current_streak}/30 days</span>
                    </div>
                    <Progress value={Math.min((progress.current_streak / 30) * 100, 100)} className="h-2" />
                    <p className="text-[10px] text-gray-400 mt-1">Next: Unstoppable (30-day streak)</p>
                  </Card>
                  <Card className="p-3">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="font-medium text-sm">Workouts</span>
                      <span className="text-xs text-gray-500">{progress.workouts_completed}/25</span>
                    </div>
                    <Progress value={Math.min((progress.workouts_completed / 25) * 100, 100)} className="h-2" />
                    <p className="text-[10px] text-gray-400 mt-1">Next: Fitness Addict (25 workouts)</p>
                  </Card>
                  <Card className="p-3">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="font-medium text-sm">Posts Created</span>
                      <span className="text-xs text-gray-500">{progress.posts_created}/20</span>
                    </div>
                    <Progress value={Math.min((progress.posts_created / 20) * 100, 100)} className="h-2" />
                    <p className="text-[10px] text-gray-400 mt-1">Next: Prolific Creator (20 posts)</p>
                  </Card>
                  <Card className="p-3">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="font-medium text-sm">XP Points</span>
                      <span className="text-xs text-gray-500">{progress.total_points}/500</span>
                    </div>
                    <Progress value={Math.min((progress.total_points / 500) * 100, 100)} className="h-2" />
                    <p className="text-[10px] text-gray-400 mt-1">Next: 500 Club</p>
                  </Card>
                </div>
              </div>
            )}

            {/* Activity Summary */}
            {progress && (
              <div className="space-y-3">
                <h3 className="text-base font-semibold flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-green-600" />
                  Activity Summary
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="text-center p-2.5 bg-blue-50 rounded-lg">
                    <MessageCircle className="h-4 w-4 text-blue-600 mx-auto mb-0.5" />
                    <div className="font-bold text-blue-600">{progress.posts_created}</div>
                    <p className="text-[10px] text-gray-500">Posts</p>
                  </div>
                  <div className="text-center p-2.5 bg-red-50 rounded-lg">
                    <Heart className="h-4 w-4 text-red-500 mx-auto mb-0.5" />
                    <div className="font-bold text-red-500">{progress.likes_given}</div>
                    <p className="text-[10px] text-gray-500">Likes Given</p>
                  </div>
                  <div className="text-center p-2.5 bg-green-50 rounded-lg">
                    <Target className="h-4 w-4 text-green-600 mx-auto mb-0.5" />
                    <div className="font-bold text-green-600">{progress.challenges_joined}</div>
                    <p className="text-[10px] text-gray-500">Challenges</p>
                  </div>
                  <div className="text-center p-2.5 bg-purple-50 rounded-lg">
                    <TrendingUp className="h-4 w-4 text-purple-600 mx-auto mb-0.5" />
                    <div className="font-bold text-purple-600">{progress.longest_streak}</div>
                    <p className="text-[10px] text-gray-500">Best Streak</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// WorkoutCalendar — Monthly calendar view of logged workouts
// ============================================================
export const WorkoutCalendar = ({ open, onClose }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [calendarData, setCalendarData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (open) fetchCalendar();
  }, [open, year, month]);

  const fetchCalendar = async () => {
    setLoading(true);
    try {
      const token = Cookies.get('session_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [calRes, progRes] = await Promise.all([
        axios.get(`${API}/workout-progress/calendar`, { params: { year, month }, headers }),
        axios.get(`${API}/workout-progress`, { headers }).catch(() => ({ data: null })),
      ]);
      setCalendarData(calRes.data);
      setProgress(progRes.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
    setSelectedDate(null);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
    setSelectedDate(null);
  };

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Build calendar grid
  const buildCalendarGrid = () => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const daysInMonth = lastDay.getDate();
    let startDow = firstDay.getDay(); // 0=Sun, adjust to Mon-start
    startDow = startDow === 0 ? 6 : startDow - 1;

    const grid = [];
    for (let i = 0; i < startDow; i++) grid.push(null);
    for (let d = 1; d <= daysInMonth; d++) grid.push(d);
    return grid;
  };

  const grid = buildCalendarGrid();
  const activeDates = new Set(calendarData?.active_dates || []);
  const selectedWorkouts = selectedDate ? (calendarData?.workouts_by_date?.[selectedDate] || []) : [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="workout-calendar-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-blue-600" />
            Workout Calendar
          </DialogTitle>
          <DialogDescription>Track your workout history and consistency</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-gray-400">Loading...</div>
        ) : (
          <div className="overflow-y-auto max-h-[70vh] space-y-4">
            {/* Streak/Stats bar */}
            {progress && (
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center py-2 bg-orange-50 rounded-lg border border-orange-200">
                  <Flame className="h-4 w-4 text-orange-500 mx-auto mb-0.5" />
                  <div className="font-bold text-orange-600">{progress.current_streak || 0}</div>
                  <p className="text-[10px] text-gray-500">Current Streak</p>
                </div>
                <div className="text-center py-2 bg-blue-50 rounded-lg border border-blue-200">
                  <Dumbbell className="h-4 w-4 text-blue-600 mx-auto mb-0.5" />
                  <div className="font-bold text-blue-600">{progress.total_workouts || 0}</div>
                  <p className="text-[10px] text-gray-500">Total Workouts</p>
                </div>
                <div className="text-center py-2 bg-green-50 rounded-lg border border-green-200">
                  <CalendarDays className="h-4 w-4 text-green-600 mx-auto mb-0.5" />
                  <div className="font-bold text-green-600">{calendarData?.total_workouts || 0}</div>
                  <p className="text-[10px] text-gray-500">This Month</p>
                </div>
              </div>
            )}

            {/* Month navigator */}
            <div className="flex items-center justify-between px-1">
              <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <h3 className="font-semibold text-gray-900">{monthNames[month - 1]} {year}</h3>
              <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-0.5">
              {dayNames.map(d => (
                <div key={d} className="text-center text-[10px] font-medium text-gray-400 py-1">{d}</div>
              ))}
              {grid.map((day, i) => {
                if (day === null) return <div key={`empty-${i}`} />;
                const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const hasWorkout = activeDates.has(dateStr);
                const isSelected = selectedDate === dateStr;
                const isToday = dateStr === new Date().toISOString().split('T')[0];
                const workoutCount = calendarData?.workouts_by_date?.[dateStr]?.length || 0;

                return (
                  <button
                    key={day}
                    onClick={() => hasWorkout ? setSelectedDate(isSelected ? null : dateStr) : null}
                    className={`relative py-2 rounded-lg text-sm transition-all ${
                      isSelected
                        ? 'bg-blue-600 text-white font-bold'
                        : hasWorkout
                          ? 'bg-green-100 text-green-800 font-medium hover:bg-green-200 cursor-pointer'
                          : isToday
                            ? 'bg-gray-100 text-gray-900 font-medium'
                            : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid={hasWorkout ? `cal-active-${dateStr}` : `cal-day-${dateStr}`}
                  >
                    {day}
                    {hasWorkout && !isSelected && (
                      <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-green-500" />
                    )}
                    {workoutCount > 1 && !isSelected && (
                      <span className="absolute top-0.5 right-0.5 text-[8px] font-bold text-green-600">{workoutCount}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Selected date detail */}
            {selectedDate && (
              <div className="space-y-2 border-t pt-3" data-testid="calendar-detail">
                <h4 className="text-sm font-semibold text-gray-900">
                  {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </h4>
                {selectedWorkouts.length === 0 ? (
                  <p className="text-sm text-gray-400">No workouts logged</p>
                ) : (
                  <div className="space-y-1.5">
                    {selectedWorkouts.map((w, i) => (
                      <div key={i} className="flex items-center gap-3 p-2.5 bg-green-50 border border-green-200 rounded-lg">
                        <Dumbbell className="h-4 w-4 text-green-600 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{w.workout_name || 'Workout'}</p>
                          {w.notes && <p className="text-xs text-gray-500 truncate">{w.notes}</p>}
                        </div>
                        {w.duration_minutes && (
                          <span className="text-xs text-gray-500 shrink-0">{w.duration_minutes} min</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Activity Logger Hook
export const useActivityLogger = () => {
  const logActivity = async (activityType, description = '') => {
    try {
      const sessionToken = Cookies.get('session_token');
      const response = await axios.post(`${API}/user/activity`, 
        { activity_type: activityType, description },
        {
          headers: { Authorization: `Bearer ${sessionToken}` },
          params: { activity_type: activityType, description }
        }
      );
      
      if (response.data.points_earned > 0) {
        toast.success(`+${response.data.points_earned} XP earned!`);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error logging activity:', error);
    }
  };

  return { logActivity };
};
